import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  FulfilmentStage,
  StageSummaryGrid,
  terminalTone,
  withMonotonicCompletion,
} from './FulfilmentStageTracker';
import { PickingConfirmationSummary } from './PickingConfimationSummary';
import { OutboundShipmentNoteSummary } from './OutboundShipmentNoteSummary';
import { ProofOfDeliverySummary } from './ProofOfDeliverySummary';
import { FulfilledExternallySummary } from './FulfilledExternallySummary';
import {
  despatchLineCount,
  despatchOrderedQuantity,
  despatchTotalProducts,
  hasProductLines,
  productsFromSkus,
  pickingProductsPicked,
  podProductsReceived,
} from '../../../utils/despatch';

export interface OutboundStageViews {
  onViewDespatch: () => void;
  onViewPicking: () => void;
  onViewShipment: () => void;
  onViewProofOfDelivery: () => void;
}

export interface OutboundStageActions {
  onAuthorise?: () => void;
  onConfirmPicking?: () => void;
  onCreateTransport?: () => void;
  onCreateShipment?: () => void;
  onCreateProofOfDelivery?: () => void;
}

const formatZoned = (
  value?: { dateTime: string; timeZone: string } | null
): string =>
  value ? dayjs.utc(value.dateTime).tz(value.timeZone).format('DD/MM/YY') : '-';

const orderedTotal = (
  process: OutboundProcess,
  isCountedInProducts: boolean
): number => {
  const { despatchNote } = process;
  if (!despatchNote) return 0;
  return isCountedInProducts
    ? despatchTotalProducts(despatchNote)
    : despatchOrderedQuantity(despatchNote);
};

const pickedTotal = (
  process: OutboundProcess,
  isCountedInProducts: boolean
): number => {
  const { pickingConfirmation } = process;
  if (!pickingConfirmation) return 0;
  return isCountedInProducts
    ? pickingProductsPicked(pickingConfirmation.skus)
    : pickingConfirmation.skus.reduce(
        (sum, sku) => sum + sku.pickedQuantity,
        0
      );
};

const shippedTotal = (
  process: OutboundProcess,
  isCountedInProducts: boolean
): number => {
  const { shipmentNote } = process;
  if (!shipmentNote) return 0;
  return isCountedInProducts
    ? productsFromSkus(shipmentNote.skus)
    : shipmentNote.skus.reduce((sum, sku) => sum + sku.skuQuantity, 0);
};

const deliveredTotal = (
  process: OutboundProcess,
  isCountedInProducts: boolean
): number => {
  const { goodsReceivedNote, shipmentNote } = process;
  if (!goodsReceivedNote) return 0;
  return isCountedInProducts
    ? podProductsReceived(goodsReceivedNote.skus, shipmentNote?.skus ?? [])
    : goodsReceivedNote.skus.reduce(
        (sum, sku) => sum + sku.receivedSkuQuantity,
        0
      );
};

export function buildOutboundStages(
  process: OutboundProcess,
  views: OutboundStageViews,
  actions: OutboundStageActions = {}
): FulfilmentStage[] {
  const { despatchNote } = process;
  const { pickingConfirmation } = process;
  const { shipmentNote } = process;
  const { goodsReceivedNote } = process;
  const { salesOrder } = process;

  const isCountedInProducts = hasProductLines(despatchNote);

  const totalItems = orderedTotal(process, isCountedInProducts);
  const totalPicked = pickedTotal(process, isCountedInProducts);
  const pickingDiscrepancyAmount = totalItems - totalPicked;
  const totalShipped = shippedTotal(process, isCountedInProducts);
  const shippingDiscrepancyAmount = totalItems - totalShipped;
  const totalDelivered = deliveredTotal(process, isCountedInProducts);
  const podDiscrepancyAmount = totalItems - totalDelivered;

  const lines = despatchNote ? despatchLineCount(despatchNote) : 0;
  const isRejected = process.status === 'Rejected';

  const orderStage: FulfilmentStage | null = salesOrder
    ? {
        key: 'order',
        pendingLabel: 'Order Received',
        doneLabel: 'Order Received',
        isComplete: true,
        summary: (
          <StageSummaryGrid
            rows={[
              {
                value:
                  salesOrder.orderNumber || salesOrder.externalOrderId || '-',
                label: 'Order #',
              },
              { value: salesOrder.customerName || '-', label: 'Customer' },
              { value: salesOrder.source || '-', label: 'Source' },
              { value: salesOrder.lines?.length ?? 0, label: 'Lines' },
            ]}
          />
        ),
      }
    : null;

  const despatchStage: FulfilmentStage = {
    key: 'despatch',
    pendingLabel: 'Confirm Despatch',
    doneLabel: 'Despatch Confirmed',
    isComplete: !!despatchNote?.isAuthorisedForPicking,
    summary: (
      <StageSummaryGrid
        rows={[
          {
            value: despatchNote?.despatchNoteNumber ?? '-',
            label: 'Despatch Note #',
          },
          {
            value: despatchNote?.purchaseOrderNumber ?? '-',
            label: 'PO Number',
          },
          {
            value: formatZoned(despatchNote?.requiredTimeOfArrival),
            label: 'Requested Arrival',
          },
          { value: lines, label: 'Lines' },
        ]}
      />
    ),
    view: { label: 'View Despatch Note', onView: views.onViewDespatch },
    action: actions.onAuthorise
      ? {
          label: 'Authorise For Picking',
          onAct: actions.onAuthorise,
          startIcon: <CheckIcon className='w-4 h-4' />,
        }
      : undefined,
  };

  const pickingStage: FulfilmentStage = {
    key: 'picking',
    pendingLabel: 'Confirm Picking',
    doneLabel: 'Picking Confirmed',
    isComplete: !!pickingConfirmation,
    summary: pickingConfirmation ? (
      <PickingConfirmationSummary
        selectedOutboundProcess={process}
        totalPickedItemsQuantity={totalPicked}
        isPickingDiscrepancy={totalPicked !== totalItems}
        pickingDiscrepancyAmount={pickingDiscrepancyAmount}
        isNegativePickingDiscrepancy={pickingDiscrepancyAmount > 0}
        hasProductLines={isCountedInProducts}
      />
    ) : undefined,
    view: pickingConfirmation
      ? { label: 'View Picking Confirmation', onView: views.onViewPicking }
      : undefined,
    action: actions.onConfirmPicking
      ? {
          label: 'Confirm Picking',
          onAct: actions.onConfirmPicking,
          startIcon: <CheckIcon className='w-4 h-4' />,
        }
      : undefined,
  };

  if (process.status === 'Fulfilled Externally') {
    return [
      ...(orderStage ? [orderStage] : []),
      {
        ...despatchStage,
        isComplete: true,
        view: despatchNote ? despatchStage.view : undefined,
        action: undefined,
      },
      { ...pickingStage, isComplete: true, action: undefined },
      {
        key: 'fulfilledExternally',
        pendingLabel: 'Fulfilled externally',
        doneLabel: 'Fulfilled externally',
        isComplete: true,
        summary: process.fulfilledExternally ? (
          <FulfilledExternallySummary
            fulfilledExternally={process.fulfilledExternally}
          />
        ) : undefined,
      },
    ];
  }

  return withMonotonicCompletion([
    ...(orderStage ? [orderStage] : []),
    despatchStage,
    pickingStage,
    {
      key: 'transport',
      pendingLabel: 'Book Transport',
      doneLabel: 'Transport Booked',
      isComplete: !!process.transportOrderId,
      summary: process.transportOrderId ? (
        <StageSummaryGrid
          rows={[
            {
              value: process.transportOrderStatus ?? 'Booked',
              label: 'Transport Status',
            },
          ]}
        />
      ) : undefined,
      action: actions.onCreateTransport
        ? {
            label: 'Create Transport Order',
            onAct: actions.onCreateTransport,
            intent: 'secondary',
            startIcon: <PlusIcon className='w-4 h-4' />,
          }
        : undefined,
      note: actions.onCreateTransport
        ? undefined
        : 'A transport order is required before creating a shipment note',
    },
    {
      key: 'shipment',
      pendingLabel: 'Confirm Shipment',
      doneLabel: 'Shipment Confirmed',
      isComplete: !!shipmentNote,
      summary: shipmentNote ? (
        <OutboundShipmentNoteSummary
          selectedOutboundProcess={process}
          totalShippedItemsQuantity={totalShipped}
          isShippingDiscrepancy={totalShipped !== totalItems}
          shippingDiscrepancyAmount={shippingDiscrepancyAmount}
          isNegativeShippingDiscrepancy={shippingDiscrepancyAmount > 0}
          hasProductLines={isCountedInProducts}
        />
      ) : undefined,
      view: shipmentNote
        ? { label: 'View Shipment Note', onView: views.onViewShipment }
        : undefined,
      action: actions.onCreateShipment
        ? {
            label: 'Create Shipment Note',
            onAct: actions.onCreateShipment,
            startIcon: <PlusIcon className='w-4 h-4' />,
          }
        : undefined,
    },
    {
      key: 'delivery',
      pendingLabel: 'Confirm Delivery',
      doneLabel: isRejected ? 'Delivery Rejected' : 'Delivery Confirmed',
      isComplete: !!goodsReceivedNote?.finalisedAt,
      doneTone: terminalTone(isRejected, podDiscrepancyAmount !== 0),
      summary: goodsReceivedNote ? (
        <ProofOfDeliverySummary
          selectedOutboundProcess={process}
          totalPODItemsQuantity={totalDelivered}
          isPODDiscrepancy={totalDelivered !== totalItems}
          podDiscrepancyAmount={podDiscrepancyAmount}
          isNegativePODDiscrepancy={podDiscrepancyAmount > 0}
          hasProductLines={isCountedInProducts}
        />
      ) : undefined,
      view: goodsReceivedNote
        ? {
            label: 'View Proof of Delivery',
            onView: views.onViewProofOfDelivery,
          }
        : undefined,
      action: actions.onCreateProofOfDelivery
        ? {
            label: 'Create Proof Of Delivery',
            onAct: actions.onCreateProofOfDelivery,
            startIcon: <PlusIcon className='w-4 h-4' />,
          }
        : undefined,
    },
  ]);
}
